import type {
  GQLAuditRuleInput,
  PublishAuditRuleInput,
  StartRuleTestRunInput,
  ValidateRuleCodeInput,
} from "../../remote/gql/graphql";
import type { AlaffiaClientModuleArgs } from "./modules";

import { createAuditRuleMutationDoc } from "../../remote/mutations/createAuditRule.gql";
import { deleteAuditRuleMutationDoc } from "../../remote/mutations/deleteAuditRule.gql";
import { publishAuditRuleMutationDoc } from "../../remote/mutations/publishAuditRule.gql";
import { startRuleTestRunMutationDoc } from "../../remote/mutations/startRuleTestRun.gql";
import { unpublishAuditRuleMutationDoc } from "../../remote/mutations/unpublishAuditRule.gql";
import { updateAuditRuleMutationDoc } from "../../remote/mutations/updateAuditRule.gql";
import { getAuditRuleQueryDoc } from "../../remote/queries/getAuditRule.gql";
import { getAuditRulesQueryDoc } from "../../remote/queries/getAuditRules.gql";
import {
  testAuditRuleResultsCsvQueryDoc,
  testAuditRuleResultsQueryDoc,
  testAuditRuleResultsSummaryQueryDoc,
} from "../../remote/queries/uiTestResults.gql";
import { validateRuleCodeQueryDoc } from "../../remote/queries/validateRuleCode.gql";
import { AlaffiaClientModule } from "./modules";

type AuditRulesModuleArgs = AlaffiaClientModuleArgs;

export class AuditRulesModule extends AlaffiaClientModule {
  constructor(args: AuditRulesModuleArgs) {
    super({ ...args, environment: "alafed" });
  }

  getAuditRules = async () => {
    const result = await this.runGqlQuery(
      getAuditRulesQueryDoc,
      {},
      "getAuditRules",
    );

    return {
      data: result.data?.auditRulesV2 ?? null,
      error: result.error ?? null,
    };
  };

  getAuditRule = async (input: { id: string }) => {
    const result = await this.runGqlQuery(
      getAuditRuleQueryDoc,
      { id: input.id },
      "getAuditRule",
    );

    return {
      data: result.data?.auditRuleV2 ?? null,
      error: result.error ?? null,
    };
  };

  createAuditRule = async (input: { input: GQLAuditRuleInput }) => {
    const result = await this.runGqlMutation(
      createAuditRuleMutationDoc,
      { input: input.input },
      "uiCreateRuleV2",
    );

    return {
      data: result.data?.uiCreateRuleV2 ?? null,
      error: result.error ?? null,
    };
  };

  updateAuditRule = async (input: { input: GQLAuditRuleInput }) => {
    const result = await this.runGqlMutation(
      updateAuditRuleMutationDoc,
      { input: input.input },
      "uiUpdateRuleV2",
    );

    return {
      data: result.data?.uiUpdateRuleV2 ?? null,
      error: result.error ?? null,
    };
  };

  deleteAuditRule = async (input: { id: string }) => {
    const result = await this.runGqlMutation(
      deleteAuditRuleMutationDoc,
      { id: input.id },
      "uiDeleteRuleV2",
    );

    return {
      data: result.data?.uiDeleteRuleV2 ?? null,
      error: result.error ?? null,
    };
  };

  startRuleTestRun = async (input: { input: StartRuleTestRunInput }) => {
    const result = await this.runGqlMutation(
      startRuleTestRunMutationDoc,
      { input: input.input },
      "startRuleTestRun",
    );

    return {
      data: result.data?.startRuleTestRun ?? null,
      error: result.error ?? null,
    };
  };

  publishAuditRule = async (input: { input: PublishAuditRuleInput }) => {
    const result = await this.runGqlMutation(
      publishAuditRuleMutationDoc,
      { input: input.input },
      "publishAuditRule",
    );

    return {
      data: result.data?.publishAuditRule ?? null,
      error: result.error ?? null,
    };
  };

  unpublishAuditRule = async (input: { input: PublishAuditRuleInput }) => {
    const result = await this.runGqlMutation(
      unpublishAuditRuleMutationDoc,
      { input: input.input },
      "unpublishAuditRule",
    );

    return {
      data: result.data?.unpublishAuditRule ?? null,
      error: result.error ?? null,
    };
  };

  runTestRules = async (input: {
    ruleId: string;
    first?: number;
    after?: string;
  }) => {
    const result = await this.runGqlQuery(
      testAuditRuleResultsQueryDoc,
      {
        ruleId: input.ruleId,
        first: input.first ?? null,
        after: input.after ?? null,
      },
      "auditTestResultsV2",
      { requestPolicy: "network-only" },
    );

    return {
      data: result.data?.auditTestResultsV2 ?? null,
      error: result.error ?? null,
    };
  };

  runTestRulesSummary = async (input: { ruleId: string }) => {
    const result = await this.runGqlQuery(
      testAuditRuleResultsSummaryQueryDoc,
      { ruleId: input.ruleId },
      "auditTestResultsSummaryV2",
      { requestPolicy: "network-only" },
    );

    return {
      data: result.data?.auditTestResultsSummaryV2 ?? null,
      error: result.error ?? null,
    };
  };

  testAuditRuleCsv = async (input: { id: string }) => {
    const result = await this.runGqlQuery(
      testAuditRuleResultsCsvQueryDoc,
      { id: input.id },
      "auditTestResultsCsvV2",
      { requestPolicy: "network-only" },
    );

    return {
      data: result.data?.auditTestResultsCsvV2 ?? null,
      error: result.error ?? null,
    };
  };

  validateRuleCode = async (input: { input: ValidateRuleCodeInput }) => {
    const result = await this.runGqlQuery(
      validateRuleCodeQueryDoc,
      { input: input.input },
      "validateRuleCode",
      { requestPolicy: "network-only" },
    );

    return {
      data: result.data?.validateRuleCode ?? null,
      error: result.error ?? null,
    };
  };
}
